"use client";

import classNames from "classnames/bind";
import styles from "./HomeSolutions.module.css";
import { useRef } from "react";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import SplitText from "gsap/SplitText";
import ScrollTrigger from "gsap/ScrollTrigger";
import Image from "next/image";
import { getImage } from "@/helpers/imageHelper";
import type { PayloadImageGroup } from "@/helpers/imageHelper";

gsap.registerPlugin(SplitText, ScrollTrigger);

const cx = classNames.bind(styles);

type Module = {
  tag: string;
  text1: string;
  text2: string;
  image: PayloadImageGroup["file"];
  videoUrl: string;
};

export default function HomeSolutions({
  tag,
  title,
  text,
  modules,
}: {
  tag: string;
  title: string;
  text: string;
  modules: Module[];
}) {
  /* useRefs */
  const containerSolutionsRef = useRef<HTMLDivElement>(null);
  const tagRef = useRef<HTMLParagraphElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const containerImagesRef = useRef<HTMLDivElement>(null);
  const pixelContainerRef = useRef<HTMLDivElement>(null);
  const wrapper1Ref = useRef<HTMLDivElement>(null);
  const wrapper2Ref = useRef<HTMLDivElement>(null);
  const wrapper3Ref = useRef<HTMLDivElement>(null);
  const image1TagRef = useRef<HTMLDivElement>(null);
  const containerTextRef = useRef<HTMLDivElement>(null);

  const image1Ref = useRef<HTMLImageElement>(null);
  const image2Ref = useRef<HTMLImageElement>(null);
  const image3Ref = useRef<HTMLImageElement>(null);
  const imageAssetRef = useRef<HTMLDivElement>(null);

  const img1 = modules[0]?.image
    ? getImage({ file: modules[0].image }, "", "full", "100vw")
    : null;

  const img2 = modules[1]?.image
    ? getImage({ file: modules[1].image }, "", "full", "100vw")
    : null;

  const img3 = modules[2]?.image
    ? getImage({ file: modules[2].image }, "", "full", "100vw")
    : null;

  useGSAP(() => {
    const container = containerSolutionsRef.current;
    const images = container?.querySelectorAll(`.${styles.wrapper}`);

    const ctx = gsap.context(() => {
      gsap.set(image2Ref.current, {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)", // initial state
      });
      gsap.set(image3Ref.current, {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)", // initial state
      });
      gsap.set(wrapper1Ref.current, {
        xPercent: -50,
      });
      gsap.set(wrapper2Ref.current, {
        xPercent: -10,
      });
      gsap.set(wrapper3Ref.current, {
        xPercent: -30,
      });

      const fadeInTl = gsap.timeline({
        scrollTrigger: {
          trigger: containerSolutionsRef.current,
          start: "top-=30% top",
          end: `+=${30}%`,
          scrub: true,
        },
      });

      // tag
      fadeInTl.fromTo(
        tagRef.current,
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: "power2.out",
        },
      );
      // title
      fadeInTl.fromTo(
        titleRef.current,
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          ease: "power2.out",
        },
        "<",
      );
      // text
      fadeInTl.fromTo(
        textRef.current,
        {
          opacity: 0,
          y: 10,
        },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
        },
        "<",
      );

      // wrapper1
      fadeInTl.fromTo(
        wrapper1Ref.current,
        {
          opacity: 0,
          y: 100,
        },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
        },
        "<",
      );
      // wrapper2
      fadeInTl.fromTo(
        wrapper2Ref.current,
        {
          opacity: 0,
          y: 80,
        },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
        },
        "<",
      );
      // wrapper3
      fadeInTl.fromTo(
        wrapper3Ref.current,
        {
          opacity: 0,
          y: 150,
        },
        {
          opacity: 1,
          y: 0,
          ease: "power2.out",
        },
        "<",
      );

      fadeInTl.fromTo(
        pixelContainerRef.current,
        {
          opacity: 0,
          yPercent: 20,
        },
        {
          opacity: 1,
          yPercent: 0,
        },
        "<",
      );

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerSolutionsRef.current,
          start: "top top",
          end: `+=${8 * 100}%`,
          scrub: true,
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            // Disable interaction if scroll is not at the top (progress > 0)
            images?.forEach((image) => {
              gsap.to(image, {
                x: 0,
                y: 0,
                duration: 0.5,
                ease: "power2.out",
              });
            });

            gsap.to(wrapper1Ref.current, {
              overflow: self.progress === 1 ? "visible" : "hidden",
              ease: "power2.out",
            });
          },
          snap: {
            snapTo: "labels", // snap to the labels
            duration: 0.8,
            ease: "power1.inOut",
          },
        },
      });
      // -----------------------------------------------------------------------
      // Animate Init transition

      // -----------------------------------------------------------------------
      // wrapper1
      tl.fromTo(
        wrapper1Ref.current,
        {
          xPercent: -50,
        },
        {
          xPercent: 0,
          ease: "power2.out",
        },
      );
      // wrapper2
      tl.fromTo(
        wrapper2Ref.current,
        {
          xPercent: -10,
        },
        {
          xPercent: 0,
          ease: "power2.out",
        },
        "<",
      );
      // wrapper3
      tl.fromTo(
        wrapper3Ref.current,
        {
          xPercent: -30,
        },
        {
          xPercent: 0,
          ease: "power2.out",
        },
        "<",
      );
      tl.addLabel("init").fromTo(
        pixelContainerRef.current,
        {
          xPercent: 0,
        },
        {
          xPercent: -10,
        },
        "<",
      );

      // -----------------------------------------------------------------------
      // Animate image1 to fullscreen
      tl.to(wrapper1Ref.current, {
        x: 0,
        y: 0,
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: 0,
        zIndex: 10,
        ease: "none",
      });
      // Animate containerImages to fullscreen
      tl.to(
        containerImagesRef.current,
        {
          maxWidth: "100%",
          ease: "power2.out",
        },
        "<",
      );
      // Animate image1Tag to fade out
      tl.to(
        image1TagRef.current,
        {
          autoAlpha: 0,
          ease: "power2.out",
        },
        "<",
      );
      tl.to(
        containerTextRef.current,
        {
          opacity: 100,
        },
        "<",
      );
      // -----------------------------------------------------------------------
      tl.to(`.${styles.image_tags}`, {
        opacity: 1,
      });
      tl.to(
        `.${styles.image_text}`,
        {
          opacity: 1,
        },
        "<",
      );
      tl.to(
        `.${styles.image_asset}`,
        {
          opacity: 1,
        },
        "<",
      );
      tl.addLabel("second").to(
        imageAssetRef.current,
        {
          autoAlpha: 1,
        },
        "<",
      );
      // -----------------------------------------------------------------------
      tl.addLabel("third").to(image2Ref.current, {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)",
      });
      // -----------------------------------------------------------------------
      tl.addLabel("fourth").to(image3Ref.current, {
        clipPath: "polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)",
      });
      tl.addLabel("last").fromTo(
        pixelContainerRef.current,
        {
          xPercent: 0,
        },
        {
          xPercent: -10,
        },
        "<",
      );
    });

    return () => {
      ctx.revert();
    };
  }, []);

  return (
    <div>
      {/* ↑ DIV Wrapper needed to parent flexbox */}
      <div ref={containerSolutionsRef} className={cx("homeSolutions")}>
        <div className={cx("container")}>
          <div ref={containerTextRef} className={cx("container_text")}>
            <div className={cx("container_text_content")}>
              <p ref={tagRef} className={cx("tag")}>
                {tag}
              </p>
              <h2 ref={titleRef} className={cx("title")}>
                {title}
              </h2>
              <p ref={textRef} className={cx("text")}>
                {text}
              </p>
            </div>
          </div>
        </div>
        <div ref={pixelContainerRef} className={cx("pixel_container")}>
          <div className={cx("pixel_image", "pixel_image1")}></div>
          <div className={cx("pixel_image", "pixel_image2")}></div>
          <div className={cx("pixel_image", "pixel_image3")}></div>
        </div>
        <div ref={containerImagesRef} className={cx("container_images")}>
          <div ref={wrapper1Ref} className={cx("wrapper", "wrapper1")}>
            <div className={cx("image_container")}>
              <div ref={image1Ref} className={cx("image", "image1")}>
                {img1?.url && (
                  <Image
                    src={img1.url}
                    alt={img1.alt ?? ""}
                    fill
                    sizes={img1.sizes}
                    aria-hidden
                    priority
                    style={{ objectFit: "cover", objectPosition: "center" }}
                  />
                )}
                <div className={cx("image_content", "js-image-content")}>
                  <div
                    className={cx(
                      "image_tags",
                      "js-image-tags",
                      "image_tags_1",
                    )}
                  >
                    <p className={cx("selected")}>{modules[0]?.tag}</p>
                    <p>{modules[1]?.tag}</p>
                    <p>{modules[2]?.tag}</p>
                  </div>
                  <div className={cx("image_text", "js-image-text")}>
                    <p>{modules[0]?.text1}</p>
                    <p>{modules[0]?.text2}</p>
                  </div>
                </div>
                <div className={cx("image_asset_wrapper", "js-image-asset")}>
                  <video
                    className={cx("image_asset")}
                    src={modules[0]?.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                  ></video>
                </div>
              </div>
              <div ref={image2Ref} className={cx("image", "image2")}>
                {img2?.url && (
                  <Image
                    src={img2.url}
                    alt={img2.alt ?? ""}
                    fill
                    sizes={img2.sizes}
                    aria-hidden
                    style={{ objectFit: "cover", objectPosition: "center" }}
                  />
                )}
                <div className={cx("image_content", "js-image-content")}>
                  <div
                    className={cx(
                      "image_tags",
                      "js-image-tags",
                      "image_tags_2",
                    )}
                  >
                    <p>{modules[0]?.tag}</p>
                    <p className={cx("selected")}>{modules[1]?.tag}</p>
                    <p>{modules[2]?.tag}</p>
                  </div>
                  <div className={cx("image_text", "js-image-text")}>
                    <p>{modules[1]?.text1}</p>
                    <p>{modules[1]?.text2}</p>
                  </div>
                </div>
                <div className={cx("image_asset_wrapper", "js-image-asset")}>
                  <video
                    className={cx("image_asset")}
                    src={modules[1]?.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                  ></video>
                </div>
              </div>
              <div ref={image3Ref} className={cx("image", "image3")}>
                {img3?.url && (
                  <Image
                    src={img3.url}
                    alt={img3.alt ?? ""}
                    fill
                    sizes={img3.sizes}
                    aria-hidden
                    style={{ objectFit: "cover", objectPosition: "center" }}
                  />
                )}
                <div className={cx("image_content", "js-image-content")}>
                  <div
                    className={cx(
                      "image_tags",
                      "js-image-tags",
                      "image_tags_2",
                    )}
                  >
                    <p>Chat</p>
                    <p>Voice</p>
                    <p className={cx("selected")}>Voice+</p>
                  </div>
                  <div className={cx("image_text", "js-image-text")}>
                    <p>{modules[2]?.text1}</p>
                    <p>{modules[2]?.text2}</p>
                  </div>
                </div>
                <div className={cx("image_asset_wrapper", "js-image-asset")}>
                  <video
                    className={cx("image_asset")}
                    src={modules[2]?.videoUrl}
                    autoPlay
                    muted
                    loop
                    playsInline
                    controls
                  ></video>
                </div>
              </div>
            </div>
            <div ref={image1TagRef} className={cx("image_tag")}>
              <p>{modules[0]?.tag}</p>
            </div>
          </div>
          <div ref={wrapper2Ref} className={cx("wrapper", "wrapper2")}>
            {img2?.url && (
              <Image
                src={img2?.url}
                alt={img2?.alt ?? ""}
                fill
                sizes={img2?.sizes}
                aria-hidden
                style={{ objectFit: "cover", objectPosition: "center" }}
              />
            )}
            <div className={cx("image_tag")}>
              <p>{modules[1]?.tag}</p>
            </div>
          </div>
          <div ref={wrapper3Ref} className={cx("wrapper", "wrapper3")}>
            {img3?.url && (
              <Image
                src={img3?.url}
                alt={img3?.alt ?? ""}
                fill
                sizes={img3?.sizes}
                aria-hidden
                style={{ objectFit: "cover", objectPosition: "center" }}
              />
            )}
            <div className={cx("image_tag")}>
              <p>{modules[2]?.tag}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
